/**
 * Provider do mecanismo de tratamento de erros baseado em https://github.com/pavadeli/angularjs-errorhandling
 * e https://technology.amis.nl/2014/10/06/automatic-error-handling-in-angularjs/ 
 *
 * Os componentes cujas chamadas de métodos precisam ser monitoradas para tratamento de erro
 * são decorados por meio do provider. A partir desse momento, exceções lançadas durante
 * a chamada do método e não capturadas e promises rejeitadas que sejam retorno dos métodos
 * são situações que serão tratadas automaticamente pelo mecanismo, integrando com o mecanismo
 * de apresentação de mensagens.
 * 
 * @author Tomás Godoi
 * @since 1.0.0
 * 
 */
namespace app.support {

    /** @ngInject */
    function errorHandlerDecorator($delegate, $injector) {
        let currentThis = null;
        function decorate($injector, obj, func) {
            
            return angular.extend(function() {
                
                let result;
                let errorHandler: ErrorHandlerService = $injector.get('errorHandler');
                
                if (!currentThis) {
                    currentThis = obj;
                    try {
                        result = errorHandler.call(func, obj, arguments);
                    } finally {
                        currentThis = null;
                    }
                } else {
                    result = func.apply(obj, arguments);
                }
                return result;
            }, func);
        }

        for (let prop in $delegate) {
            if (angular.isFunction($delegate[prop])) {
                $delegate[prop] = decorate($injector, $delegate, $delegate[prop]);
            }
        }
        return $delegate;
    }

    export class ErrorHandlerService {

        /** @ngInject */
        constructor(private messagesService: app.support.messaging.MessagesService, private errorService: ErrorService) {

        }

        public call(func: Function, self, args) {
            let result;
            try {
                result = func.apply(self, args);
            } catch (error) {
                this.handleError(func, error);
                throw error;
            }

            let promise = result && result.$promise || result;
            if (promise && angular.isFunction(promise.then) && angular.isFunction(promise['catch'])) {
                this.handleAsyncError(func, promise);
            }

            return result;
        }

        private handleError(func: Function, err) {
            // TODO Melhorar a detecção de vários tipos de erros.
            this.messagesService.error(err);
        }

        private handleAsyncError(func: Function, promise) {
            promise['catch']((error) => {
                if (error) {
                    let actualError;
                    if (error.hasOwnProperty("headers")) {
                        actualError = error.data;
                    } else {
                        actualError = error;
                    }
                    if (!promise["$stfNoErrorMessage"]) {
                        this.messagesService.error(this.errorService.buildErrorMessages(actualError));
                    }
                }
            });
            return promise;
        }

    }

    /**
     * Decorator que permite detectar erros e exibir automaticamente a mensagem de erro para todos
     * os métodos de um service.
     * 
     * No caso de erros em chamadas assíncronas, pode-se utilizar a propriedade $stfNoErrorMessage
     * na promise de retorno do método para não exibir a mensagem de erro, caso se deseje tratar
     * o erro de forma diferente em algum método específico.
     */
    export class ErrorHandlerServiceProvider implements ng.IServiceProvider {

        public decorate($provide: ng.auto.IProvideService, service): void {
            $provide.decorator(service, errorHandlerDecorator);
        }

        /** @ngInject */
        public $get(messagesService: app.support.messaging.MessagesService, errorService: ErrorService): ErrorHandlerService {
            return new ErrorHandlerService(messagesService, errorService);
        }

    }

    angular
        .module('app.support')
        .provider('errorHandler', ErrorHandlerServiceProvider);

}